/**
 * @genesis_domain User Preferences — Hybrid Sync
 * @genesis_version V9.1
 * @genesis_intent Defines the UserPreferencesSchema, UserProfileSchema, and UpdatePreferencesSchema
 *   Zod contracts for the public.user_profiles JSONB preferences column. The preferences JSONB
 *   column houses all cross-device user-scoped settings (Export configurations, Theme overrides,
 *   etc.). Managed via TanStack Query optimistic mutations on the frontend.
 * @genesis_boundaries FORBIDDEN from containing mutation logic or database queries. Pure validation only.
 *   FORBIDDEN from using Logical OR (||) for fallbacks — Nullish Coalescing (??) only.
 *   FORBIDDEN from duplicating API DTOs that reside in shared/schemas/api.ts — those are
 *   request/response contracts; this file defines the domain entity shape.
 * @genesis_ai_trail V1.0: Created as Phase 1 of the Hybrid Sync user preferences system.
 *   The preferences JSONB column is the single source of truth for all user-scoped settings.
 *   Current initial shape: export.defaultFormat only. Extensible for future preference categories.
 */

import { z } from 'zod';

import { TimestampSchema } from './common.js';

/**
 * User Preferences — the JSONB shape stored in public.user_profiles.preferences.
 *
 * Each top-level key represents a preference category. All keys are optional to support
 * partial updates and backward compatibility when new categories are added.
 *
 * Current categories:
 * - export: Export configuration (default format for JSON-LD vs PDF exports)
 *
 * Future categories (stubs):
 * - theme: Per-user theme overrides (currently theme_id lives on workspace_members)
 * - display: Display density, animation preferences
 * - notifications: Notification channel preferences
 */
export const UserPreferencesSchema = z.object({
    export: z.object({
        defaultFormat: z.enum(['json-ld', 'pdf', 'xlsx']).default('json-ld'),
        includeImages: z.boolean().default(true),
        includeNutrition: z.boolean().default(true),
        includeDescription: z.boolean().default(true),
        includeMeta: z.boolean().default(true),
        includeIngredients: z.boolean().default(true),
        includeInstructions: z.boolean().default(true),
        includeSource: z.boolean().default(true),
    }).optional(),
    appearance: z.object({
        theme: z.string().optional(),
    }).optional(),
    measurements: z.object({
        units: z.enum(['us', 'metric']).default('us'),
    }).optional(),
}).strict();

export type UserPreferences = z.infer<typeof UserPreferencesSchema>;

/**
 * Full user_profiles row schema.
 * Mirrors the public.user_profiles table with snake_case → camelCase mapping
 * applied at the transformer layer.
 */
export const UserProfileSchema = z.object({
    userId: z.string().uuid(),
    preferences: UserPreferencesSchema.default({}),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});

export type UserProfile = z.infer<typeof UserProfileSchema>;

/**
 * PATCH /api/preferences request body schema.
 * Accepts a partial or full preferences object to merge into the existing JSONB column.
 * Using UserPreferencesSchema (which has all optional sub-objects) enables granular updates.
 */
export const UpdatePreferencesSchema = z.object({
    preferences: UserPreferencesSchema,
}).strict();

export type UpdatePreferences = z.infer<typeof UpdatePreferencesSchema>;
